import React, { useEffect, useState } from "react";
import { X, CheckCircle, XCircle, ClockCounterClockwise, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { logError } from "@/lib/logger";

const STATUS_OPTS = [
    {
        key: "selected",
        label: "Selected",
        icon: CheckCircle,
        chip: "bg-emerald-50 text-emerald-700 border-emerald-200",
        blurb: "Candidate passed the interview — record feedback before advancing to Offer.",
    },
    {
        key: "rejected",
        label: "Rejected",
        icon: XCircle,
        chip: "bg-rose-50 text-rose-700 border-rose-200",
        blurb: "Candidate didn't clear — feedback is required. This will move them to Rejected stage.",
    },
    {
        key: "rescheduled",
        label: "Rescheduled",
        icon: ClockCounterClockwise,
        chip: "bg-amber-50 text-amber-700 border-amber-200",
        blurb: "Interview needs to be moved — set a new slot and (optionally) a reason.",
    },
];

/**
 * Records an Interview stage sub-status: Selected / Rejected / Rescheduled.
 * @param {string} candidateId
 * @param {object} candidate  Current candidate document (for reschedule count + prior outcome)
 */
export default function InterviewOutcomeModal({ candidateId, candidate, onClose, onSaved }) {
    const existing = candidate?.interview_outcome || null;
    const [status, setStatus] = useState(existing?.status || "selected");
    const [feedback, setFeedback] = useState(existing?.feedback || "");
    const [score, setScore] = useState(existing?.score || "");
    const [nextSlot, setNextSlot] = useState(existing?.next_slot_at ? existing.next_slot_at.slice(0, 16) : "");
    const [reason, setReason] = useState(existing?.reason || "");
    const [saving, setSaving] = useState(false);

    const rescheduleCount = candidate?.interview_reschedule_count || 0;
    const willBeCount = rescheduleCount + 1;
    const reasonRequired = status === "rescheduled" && willBeCount >= 3;

    // Default next_slot_at to +2 days when Rescheduled first selected
    useEffect(() => {
        if (status === "rescheduled" && !nextSlot) {
            const d = new Date();
            d.setDate(d.getDate() + 2);
            d.setMinutes(0);
            d.setSeconds(0);
            setNextSlot(d.toISOString().slice(0, 16));
        }
    }, [status, nextSlot]);

    const active = STATUS_OPTS.find((o) => o.key === status) || STATUS_OPTS[0];
    const Icon = active.icon;

    const canSave = () => {
        if (status === "selected") return feedback.trim().length > 0;
        if (status === "rejected") return feedback.trim().length > 0;
        if (status === "rescheduled") {
            if (!nextSlot) return false;
            if (reasonRequired && !reason.trim()) return false;
            return true;
        }
        return false;
    };

    const handleSave = async () => {
        if (saving || !canSave()) return;
        setSaving(true);
        const payload = { status };
        if (status === "selected") {
            payload.feedback = feedback.trim();
            if (score) payload.score = Number(score);
        } else if (status === "rejected") {
            payload.feedback = feedback.trim();
        } else {
            payload.next_slot_at = new Date(nextSlot).toISOString();
            if (reason.trim()) payload.reason = reason.trim();
        }
        try {
            const r = await api.post(`/candidates/${candidateId}/interview-outcome`, payload);
            toast.success(
                status === "selected" ? "Marked as Selected — ready to move to Offer" :
                status === "rejected" ? "Marked as Rejected · moved to Rejected stage" :
                `Rescheduled (reschedule #${willBeCount})`
            );
            onSaved?.(r.data);
            onClose?.();
        } catch (ex) {
            logError("interview-outcome-save-failed", ex);
            const detail = ex?.response?.data?.detail || "Could not save outcome.";
            toast.error(detail);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="interview-outcome-modal"
            className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[#E5E5E5]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-[#E5E5E5] px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className={`inline-flex items-center justify-center w-9 h-9 border ${active.chip}`}>
                            <Icon size={16} weight="bold" />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">
                                Interview outcome
                            </p>
                            <h2 className="font-heading text-lg font-black text-ink">Record decision</h2>
                        </div>
                    </div>
                    <button
                        data-testid="interview-outcome-close"
                        onClick={onClose}
                        className="p-1.5 hover:bg-[#F5F5F5] transition-colors"
                    >
                        <X size={16} weight="bold" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-5">
                    {/* Status picker */}
                    <div className="grid grid-cols-3 gap-2">
                        {STATUS_OPTS.map((o) => {
                            const OIcon = o.icon;
                            const isActive = o.key === status;
                            return (
                                <button
                                    key={o.key}
                                    data-testid={`interview-status-${o.key}`}
                                    onClick={() => setStatus(o.key)}
                                    className={`border p-3 flex flex-col items-center gap-1.5 transition-all ${
                                        isActive
                                            ? `${o.chip} border-current`
                                            : "border-[#E5E5E5] hover:border-[#A3A3A3] text-[#525252]"
                                    }`}
                                >
                                    <OIcon size={18} weight="bold" />
                                    <span className="text-xs font-bold">{o.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    <p className="text-xs text-[#525252] leading-relaxed">{active.blurb}</p>

                    {/* SELECTED / REJECTED — feedback */}
                    {(status === "selected" || status === "rejected") && (
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-[#525252]">
                                {status === "selected" ? "What went well?" : "Reason for rejection"}
                                <span className="text-rose-500 ml-1">*</span>
                            </label>
                            <textarea
                                data-testid="interview-feedback"
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                rows={4}
                                maxLength={800}
                                placeholder={
                                    status === "selected"
                                        ? "e.g. Strong system design fundamentals, confident communication, good culture fit."
                                        : "e.g. Weak on distributed systems questions, poor code readability under pressure."
                                }
                                className="w-full border border-[#E5E5E5] p-3 text-sm focus:outline-none focus:border-ink"
                            />
                            <p className="text-[10px] text-[#A3A3A3] text-right">{feedback.length}/800</p>
                        </div>
                    )}

                    {/* SELECTED — optional score */}
                    {status === "selected" && (
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-[#525252]">
                                Score (1–10, optional)
                            </label>
                            <input
                                data-testid="interview-score"
                                type="number"
                                min={1}
                                max={10}
                                value={score}
                                onChange={(e) => setScore(e.target.value)}
                                className="w-24 border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink"
                            />
                        </div>
                    )}

                    {/* RESCHEDULED — next slot + reason */}
                    {status === "rescheduled" && (
                        <>
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-wider text-[#525252]">
                                    New interview slot <span className="text-rose-500 ml-1">*</span>
                                </label>
                                <input
                                    data-testid="interview-next-slot"
                                    type="datetime-local"
                                    value={nextSlot}
                                    onChange={(e) => setNextSlot(e.target.value)}
                                    className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink"
                                />
                            </div>

                            {rescheduleCount > 0 && (
                                <div className={`border p-3 flex items-start gap-2 text-xs ${
                                    willBeCount >= 3 ? "bg-rose-50 border-rose-200 text-rose-900" : "bg-amber-50 border-amber-200 text-amber-900"
                                }`}>
                                    <Warning size={14} weight="bold" className="shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-bold">
                                            This will be reschedule #{willBeCount}.
                                        </p>
                                        {willBeCount >= 3 && (
                                            <p className="mt-1">
                                                A written reason is now required. Consider whether the candidate should instead be marked Rejected.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-wider text-[#525252]">
                                    Reason {reasonRequired && <span className="text-rose-500">*</span>}
                                    {!reasonRequired && <span className="text-[#A3A3A3] font-normal normal-case ml-1">(optional)</span>}
                                </label>
                                <textarea
                                    data-testid="interview-reschedule-reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={3}
                                    maxLength={400}
                                    placeholder="e.g. Candidate requested; personal emergency."
                                    className="w-full border border-[#E5E5E5] p-3 text-sm focus:outline-none focus:border-ink"
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-[#E5E5E5] px-6 py-4 bg-[#FAFAFA]">
                    <button
                        onClick={onClose}
                        data-testid="interview-outcome-cancel"
                        className="text-xs font-bold uppercase tracking-wider text-[#525252] px-4 py-2 hover:bg-[#E5E5E5] transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        data-testid="interview-outcome-save"
                        disabled={saving || !canSave()}
                        className="btn-gradient text-xs font-bold uppercase tracking-wider px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {saving ? "Saving…" : "Save outcome"}
                    </button>
                </div>
            </div>
        </div>
    );
}
